import axios from "axios";
import { getApiUrl } from "../utils/constants";
import {
  handleEncodeJson,
  handleDecodeJSON,
} from "../utils/encodeDecodeJson";
import { decryptExamPageResponseContent } from "./dataService";
import {
  bytesToHex,
  decryptData,
  decryptKeyToUtf8,
  getAppKeyBytes,
} from "../utils/cryptoHelpers";
import { SECURITY_LEVEL } from "../config/appConstants";

const uploadAxios = axios.create();

uploadAxios.interceptors.request.use((config) => {
  const token = axios.defaults.headers.common["authorization"];
  if (token) {
    config.headers["authorization"] = token;
  }
  return config;
});


const buildUrl = (path, token) => {
  const base = getApiUrl();
  return `${base}${path}?token=${token}`;
};


const handleResponse = async (result) => {
  const response = result?.data;

  if (response?.code && response.code !== "OK") {
    const error = new Error(response?.message || "Something went wrong");
    error.code = response.code;
    throw error;
  }

  return handleDecodeJSON({ decodeJSON: response?.data });
};


// Reached from the dashboard, the candidate never went through candidateLogin, so
// there is no session_context. The questions response carries the keys for this
// exam (flag/blob beside the envelope, the rest inside it); build the context from them.
const restoreSessionContextFromResponse = async (responseData) => {
  const appKeyBytes = getAppKeyBytes();
  const reqResDekBytes = await decryptData(bytesToHex(appKeyBytes), responseData.blob);
  // No envelope key when request encryption is off; the body is then already plain.
  let decodeJSON = responseData;
  if (responseData.key) {
    const unwrappedKey = new TextDecoder("utf-8").decode(
      await decryptData(bytesToHex(reqResDekBytes), responseData.key)
    );
    decodeJSON = { ...responseData, key: unwrappedKey };
  }
  const decoded = await handleDecodeJSON({ decodeJSON });
  const securityLevel = await decryptKeyToUtf8(appKeyBytes, responseData.flag);

  if (securityLevel && securityLevel !== SECURITY_LEVEL.NONE) {
    sessionStorage.setItem(
      "session_context",
      btoa(
        JSON.stringify({
          flag: responseData.flag,
          blob: responseData.blob,
          localProof: decoded?.clientDevicePvtKey || null,
          remoteAnchor: decoded?.serverPubKey || null,
          sessionSeal: decoded?.candidateDataEncKey || null,
        })
      )
    );
  }
  return decoded;
};

export const fetchPostExamQuestions = async (token, language) => {
  const url = buildUrl(
    "/api/exam/descriptive-upload/post-exam/questions",
    token
  );
  const params = language ? { language } : {};
  const result = await uploadAxios.get(url, { params });
  const response = result?.data;

  if (response?.code && response.code !== "OK") {
    return handleResponse(result);
  }

  const responseData = response?.data;
  if (responseData?.flag && responseData?.blob) {
    return decryptExamPageResponseContent(await restoreSessionContextFromResponse(responseData));
  }

  const decoded = await handleDecodeJSON({ decodeJSON: responseData, unwrapKey: true });
  return decryptExamPageResponseContent(decoded);
};



export const uploadAnswerFile = async (file, srNo, token) => {
  const url = buildUrl(
    "/api/exam/descriptive-upload/post-exam/upload",
    token
  );
  const encodedPayload = await handleEncodeJson({ encodeJSON: { srNo } });
  const formData = new FormData();
  formData.append("file", file);
  Object.entries(encodedPayload).forEach(([key, val]) => {
    formData.append(key, val);
  });
  const result = await uploadAxios.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return handleResponse(result);
};

// ---------------------------------------------------------------------------
// deleteAnswerFile
// ---------------------------------------------------------------------------


export const deleteAnswerFile = async (srNo, token) => {
  const url = buildUrl(
    "/api/exam/descriptive-upload/post-exam/delete",
    token
  );
  const encodedBody = await handleEncodeJson({ encodeJSON: { srNo } });
  const result = await uploadAxios.post(url, encodedBody);
  return handleResponse(result);
};

// ---------------------------------------------------------------------------
// submitUploadedAnswers
// ---------------------------------------------------------------------------


export const submitUploadedAnswers = async (token) => {
  const url = buildUrl(
    "/api/exam/descriptive-upload/post-exam/submit",
    token
  );
  const result = await uploadAxios.post(url);
  return handleResponse(result);
};